import React, { useCallback, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { Alert, EmptyState, MenuItem, Spinner, Text } from "@performyard/py-components";
import { EmployeeSummary, Timeframe } from "@performyard/service-contracts";

import { TimeframeToggle } from "./components/TimeframeToggle";

import {
	useAiSettings,
	useCompanyFunction,
	useEmployeeSummary,
	useInternalApiUser,
	useRbacEmployeePermissions,
} from "../../../api/hooks";
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";
import { useScrollSpy } from "./useScrollSpy";

import { SECTIONS, SectionGates, SectionId } from "./config";
import { CoachingSection } from "./sections/CoachingSection";
import { CompetenciesSection } from "./sections/CompetenciesSection";
import { FeedbackSection } from "./sections/FeedbackSection";
import { GoalsSection } from "./sections/GoalsSection";
import { MeetingsSection } from "./sections/MeetingsSection";
import { ReviewsSection } from "./sections/ReviewsSection";
import { SummarySection } from "./sections/SummarySection";

// Sticky/anchor offset that clears the side-nav layout's fixed 48px top bar
// (var only defined under body[data-layout="sidenav"]; falls back to 0 on legacy
// nav) plus the standard 1.5rem gap. Keeps the sticky section nav and click-to-
// section anchors from sliding under the top bar. See webserver base.html.
const TOP_BAR_STICKY_OFFSET = "calc(var(--pyo-topbar-height, 0px) + 1.5rem)";

const UpdatingIndicator: React.FC = () => (
	<div className="flex items-center gap-2" data-testid="insights-updating">
		<Spinner size={16} />
		<Text size="paragraph-3" color="neutral-5">
			Updating with the latest…
		</Text>
	</div>
);

const SectionPlaceholder: React.FC<{ label: string }> = ({ label }) => (
	<div className="mt-2">
		<EmptyState variant="illustration" option={2} label={`No ${label.toLowerCase()} data yet`} />
	</div>
);

const renderSectionBody = (id: SectionId, label: string, summary: EmployeeSummary): React.ReactNode => {
	switch (id) {
		case "summary":
			return <SummarySection summary={summary} />;
		case "coaching":
			return <CoachingSection summary={summary} />;
		case "competencies":
			return <CompetenciesSection summary={summary} />;
		case "reviews":
			return <ReviewsSection summary={summary} />;
		case "feedback":
			return <FeedbackSection summary={summary} />;
		case "goals":
			return <GoalsSection summary={summary} />;
		case "meetings":
			return <MeetingsSection summary={summary} />;
		default:
			return <SectionPlaceholder label={label} />;
	}
};

export const EmployeeInsights: React.FC = () => {
	const { userID = "" } = useParams<{ userID: string }>();
	const [timeframe, setTimeframe] = useState<Timeframe>("all");

	const { data: insightsPermissions } = useRbacEmployeePermissions(userID, "employee_summary", !userID);
	const hasInsightsPermission = useMemo(
		() =>
			(insightsPermissions ?? []).some((permission) =>
				[
					"employee_summary:viewSelf",
					"employee_summary:viewAsManager",
					"employee_summary:viewAsAdmin",
				].includes(permission)
			),
		[insightsPermissions]
	);
	const isSelfView = useMemo(() => {
		const permissions = insightsPermissions ?? [];
		const canViewSelf = permissions.includes("employee_summary:viewSelf");
		const canViewAsOther =
			permissions.includes("employee_summary:viewAsManager") ||
			permissions.includes("employee_summary:viewAsAdmin");
		return canViewSelf && !canViewAsOther;
	}, [insightsPermissions]);

	// Gate the fetch on a resolved view permission so an unauthorized deep-link doesn't
	// fire a guaranteed-403 request (and the global error toast) before the layout's 403
	// page renders.
	const { data, isLoading } = useEmployeeSummary(userID, timeframe, { enabled: hasInsightsPermission });

	const { data: userInfo } = useInternalApiUser(userID, { enabled: hasInsightsPermission });

	const reviews = useCompanyFunction("reviews");
	const goals = useCompanyFunction("goals");
	const feedback = useCompanyFunction("feedback");
	const reviewsFlag = useFeatureFlagQuery("ff-is-reviews-enabled", false);
	const goalsFlag = useFeatureFlagQuery("ff-is-goals-enabled", false);
	const feedbackFlag = useFeatureFlagQuery("ff-is-feedback-enabled", false);
	const competenciesFlag = useFeatureFlagQuery("ff-is-competency-management-enabled", false);
	const meetingsFlag = useFeatureFlagQuery("ff-is-meetings-enabled", false);
	const coachingFlag = useFeatureFlagQuery("ff-is-coaching-enabled", false);
	const aiSettings = useAiSettings();

	const gates: SectionGates = useMemo(
		() => ({
			// Each domain section requires BOTH its feature flag AND the company setting,
			// matching the platform pattern (e.g. pages/Goals/Employee) so insights can't
			// show a module that's hidden everywhere else.
			reviewsEnabled: reviewsFlag.value === true && reviews.companyFunction === "enabled",
			goalsEnabled: goalsFlag.value === true && goals.companyFunction === "enabled",
			feedbackEnabled: feedbackFlag.value === true && feedback.companyFunction === "enabled",
			competenciesEnabled: competenciesFlag.value === true,
			meetingsEnabled: meetingsFlag.value === true,
			coachingEnabled: coachingFlag.value === true && aiSettings.data?.employeeCoachingEnabled === true,
			isSelfView,
		}),
		[
			reviews.companyFunction,
			goals.companyFunction,
			feedback.companyFunction,
			reviewsFlag.value,
			goalsFlag.value,
			feedbackFlag.value,
			competenciesFlag.value,
			meetingsFlag.value,
			coachingFlag.value,
			aiSettings.data?.employeeCoachingEnabled,
			isSelfView,
		]
	);

	const visibleSections = useMemo(() => SECTIONS.filter((section) => section.isVisible(gates)), [gates]);

	const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

	// Enable the scroll-spy only once the summary has loaded, since that's when the
	// section elements are rendered and their refs are populated (see useScrollSpy).
	const { activeId: activeSectionId, setActive } = useScrollSpy(
		visibleSections.map((section) => section.id),
		sectionRefs,
		Boolean(data)
	);

	const handleNavClick = useCallback(
		(id: SectionId) => () => {
			// Highlight the clicked section immediately; the scroll-spy observer is
			// suppressed during the smooth scroll so it doesn't flicker through the
			// sections in between and land on a neighbor (e.g. Competencies).
			setActive(id);
			sectionRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
		},
		[setActive]
	);

	// Name the subject in the Summary card so it reads as a summary *of someone*; the nav
	// keeps the short "Summary" label. Falls back to the plain title until the name loads.
	const sectionTitle = useCallback(
		(section: (typeof SECTIONS)[number]): string => {
			const title = section.cardTitle ?? section.label;
			if (section.id !== "summary" || !userInfo?.name) {
				return title;
			}
			return `${title} of ${userInfo.name}`;
		},
		[userInfo?.name]
	);

	const status = data?.generation?.status;
	const isInProgress = status === "in_progress";
	const isFailed = status === "failed";
	const isRefreshing = !isFailed && (isInProgress || data?.is_stale === true);

	return (
		<div className="mt-6 flex flex-col items-center" data-testid="employee-insights" data-user-id={userID}>
			{/* Match the Employee Details page container width (880px) so the tab content
			    lines up with the breadcrumb + tab bar and doesn't jump between tabs. */}
			<div className="w-[880px] max-w-full">
				{(isLoading || !hasInsightsPermission) && !data ? (
					<div className="mt-6 flex justify-center" data-testid="insights-loading">
						<Spinner size={32} />
					</div>
				) : isFailed ? (
					<div className="flex flex-col gap-6" data-testid="insights-failed">
						<TimeframeToggle value={timeframe} onChange={setTimeframe} />
						<Alert type="error" message="Couldn't generate insights." />
					</div>
				) : data ? (
					<div className="flex gap-4">
						<nav
							className="sticky box-border hidden h-fit w-52 shrink-0 flex-col gap-1 rounded-lg bg-neutral-0 p-4 md:flex"
							style={
								{
									top: TOP_BAR_STICKY_OFFSET,
									"--sidenav-active-bg": "var(--py-neutral-1)",
								} as React.CSSProperties
							}
							aria-label="Insights sections"
							data-testid="insights-nav"
						>
							{visibleSections.map((section) => (
								<MenuItem
									key={section.id}
									variant="nested-link"
									label={section.label}
									isActive={activeSectionId === section.id}
									onClick={handleNavClick(section.id)}
									dangerouslySetClassName="mx-0 h-7 rounded px-2 font-normal"
									data-testid={`insights-nav-${section.id}`}
								/>
							))}
						</nav>

						<div className="flex min-w-0 flex-1 flex-col gap-6">
							{visibleSections.map((section) => (
								<React.Fragment key={section.id}>
									<section
										id={section.id}
										ref={(el) => {
											sectionRefs.current[section.id] = el;
										}}
										className="rounded-lg bg-neutral-0 px-8 py-6"
										style={{ scrollMarginTop: TOP_BAR_STICKY_OFFSET }}
										aria-label={section.label}
										data-testid={`insights-section-${section.id}`}
									>
										<div className="flex items-center justify-between gap-4">
											<Text size="sub-heading" weight="semi-bold" overrideElement="h2">
												{sectionTitle(section)}
											</Text>
											{isRefreshing ? <UpdatingIndicator /> : null}
										</div>
										{renderSectionBody(section.id, section.label, data)}
									</section>
									{section.id === "summary" ? (
										<TimeframeToggle value={timeframe} onChange={setTimeframe} />
									) : null}
								</React.Fragment>
							))}
						</div>
					</div>
				) : null}
			</div>
		</div>
	);
};

export default EmployeeInsights;
